import {startOfWeek, subWeeks} from "date-fns";
import {zonedTimeToUtc} from "date-fns-tz";
import {saveAs} from "file-saver";
import * as XLSX from "xlsx";
import Big from "big.js";
import {v4 as uuid} from "uuid";

export function getTotalPageViewsByWeek(week, data) {
  return data
    .filter((item) => item["Params_Event.eventDate.week"] === week)
    .reduce((sum, item) => sum + item["Params_Event.verified_reads"], 0);
}

export function getCurrentWeekStartDate() {
  const today = new Date();
  return zonedTimeToUtc(startOfWeek(today, {weekStartsOn: 1}));
}

export function formatDateToISOString(d) {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00.000`;
}

export function getAverageTimeSpentOnPageForWeek(week, data) {
  const filteredData = data.filter(
    (item) => item["Params_Event.eventDate.week"] === week
  );

  const totalTimeSpentOnPage = filteredData.reduce((total, current) => {
    return total + (current["Params_Event.averageTimeSpentOnPage"] || 0);
  }, 0);

  return totalTimeSpentOnPage / filteredData.length;
}

export function getImpressionsByWeek(dataArray, targetWeek) {
  const foundObject = dataArray.find(
    (item) => item["epom_data.Date.week"] === targetWeek
  );
  return foundObject ? foundObject["epom_data.Impressions"] : 0;
}

export function getPreviousWeekStartDate() {
  return subWeeks(getCurrentWeekStartDate(), 1);
}

export function getChange(current, prev) {
  let totalChange;
  const currentNumber = parseFloat(current);
  const prevNumber = parseFloat(prev);

  if (
    isNaN(currentNumber) ||
    isNaN(prevNumber) ||
    currentNumber === 0 ||
    prevNumber === 0
  ) {
    totalChange = "N/A";
  } else {
    totalChange = (currentNumber / prevNumber - 1) * 100;
  }
  return totalChange;
}

export function transformVideoData(data) {
  if (!data) {
    return [];
  }
  const filteredData = data.filter(
    (item) => item["Params_Event.videoTitle"] !== null
  );

  let videoDataMap = {};

  filteredData.forEach((item) => {
    if (!videoDataMap[item["Params_Event.videoTitle"]]) {
      videoDataMap[item["Params_Event.videoTitle"]] = {
        title: item["Params_Event.videoTitle"],
        starts: 0,
        video_progress_25_percent: 0,
        video_progress_50_percent: 0,
        video_progress_75_percent: 0,
        video_complete: 0,
        count: 0,
      };
    }

    videoDataMap[item["Params_Event.videoTitle"]].starts +=
      item["Params_Event.video_start"];
    videoDataMap[item["Params_Event.videoTitle"]].video_progress_25_percent +=
      item["Params_Event.video_progress_25_percent"];
    videoDataMap[item["Params_Event.videoTitle"]].video_progress_50_percent +=
      item["Params_Event.video_progress_50_percent"];
    videoDataMap[item["Params_Event.videoTitle"]].video_progress_75_percent +=
      item["Params_Event.video_progress_75_percent"];
    videoDataMap[item["Params_Event.videoTitle"]].video_complete +=
      item["Params_Event.video_complete"];
    videoDataMap[item["Params_Event.videoTitle"]].count++;
  });

  let result = [];

  for (const [key, value] of Object.entries(videoDataMap)) {
    const {
      starts,
      video_progress_25_percent,
      video_progress_50_percent,
      video_progress_75_percent,
      video_complete,
      count,
    } = value;

    const pct25 =
      starts > 0
        ? ((video_progress_25_percent / starts) * 100).toFixed(0) + "%"
        : "0%";
    const pct50 =
      starts > 0
        ? ((video_progress_50_percent / starts) * 100).toFixed(0) + "%"
        : "0%";
    const pct75 =
      starts > 0
        ? ((video_progress_75_percent / starts) * 100).toFixed(0) + "%"
        : "0%";
    const completes =
      starts > 0 ? ((video_complete / starts) * 100).toFixed(0) + "%" : "0%";

    result.push({
      key: result.length + 1,
      title: key,
      starts: starts,
      pct25: pct25,
      pct50: pct50,
      pct75: pct75,
      completes: completes,
    });
  }

  return result;
}

export function transformWebsiteVideoData(data, websiteId) {
  if (!data) {
    return [];
  }

  const result = data
    .filter((item) => item["Params_Event.videoTitle"] !== null)
    .filter(
      (item) => item["Params_Event.contimoAdvertorialSiteId"] === websiteId
    )
    .reduce((acc, item, index) => {
      const starts = item["Params_Event.video_start"];

      const pct25 =
        starts > 0
          ? (
          (item["Params_Event.video_progress_25_percent"] / starts) *
          100
        ).toFixed(0) + "%"
          : "0%";

      const pct50 =
        starts > 0
          ? (
          (item["Params_Event.video_progress_50_percent"] / starts) *
          100
        ).toFixed(0) + "%"
          : "0%";

      const pct75 =
        starts > 0
          ? (
          (item["Params_Event.video_progress_75_percent"] / starts) *
          100
        ).toFixed(0) + "%"
          : "0%";

      const completes =
        starts > 0
          ? ((item["Params_Event.video_complete"] / starts) * 100).toFixed(0) +
          "%"
          : "0%";

      acc.push({
        key: index + 1,
        title: item["Params_Event.videoTitle"],
        starts: starts,
        pct25: pct25,
        pct50: pct50,
        pct75: pct75,
        completes: completes,
      });

      return acc;
    }, []);

  // If result array is empty, return an array containing the fallback object.
  return result.length === 0
    ? [
      {
        /* fallback object structure */
      },
    ]
    : result;
}

export function transformEpomData(epomData, apiData, websiteId, websiteName) {
  if (!epomData || !apiData) return [];

  // Create a map for easy lookup of headlines by trafficDriverId.
  const apiDataMap = {};
  apiData.forEach((item) => {
    apiDataMap[item._id] = item.headline;
  });

  // Filter based on websiteId and map headlines.
  const result = epomData
    .filter((item) => item["epom_data.websiteId"] === websiteId)
    .map((item) => {
      const headline = apiDataMap[item["epom_data.trafficDriverId"]] || "";
      const impressions = item["epom_data.Impressions"];
      const clicks = item["epom_data.Click"];
      const reads = item["epom_data.Reads"];
      const tdCost = item["epom_data.totalCost"];
      let clickRate = impressions && clicks ? (clicks / impressions) * 100 : 0;
      clickRate = clickRate.toFixed(1);
      clickRate = clickRate.toString() + "%";
      return {
        ...item,
        headline: headline,
        clickRate: clickRate,
        tdReads: reads,
        tdCost: tdCost,
      };
    });
  return result;
}

export function transformAggData(aggData, websiteId, websiteName) {
  if (!aggData) return [];

  // Filter based on websiteId and map headlines.
  const result = aggData
    .filter((item) => item["aggregateTdLevel.websiteId"] === websiteId)
    .map((item) => {
      let clickRate = item["aggregateTdLevel.tdImpressions"] && item["aggregateTdLevel.tdClicks"] ? (item["aggregateTdLevel.tdClicks"] / item["aggregateTdLevel.tdImpressions"]) * 100 : 0;
      clickRate = clickRate.toFixed(1);
      clickRate = clickRate.toString() + "%";
      return {
        ...item,
        clickRate: clickRate,
      };
    });
  return result;
}


export function mergePubsCost(pubList, costList) {
  let output = {};

  pubList.forEach((pub) => {
    // For each publication, create or update the publisher entry
    if (!output[pub.publisher._id]) {
      output[pub.publisher._id] = {
        name: pub.publisher.name,
        advertorialSites: {},
      };
    }

    // For each advertorial site, create or update the advertorial site entry
    if (pub.advertorialSite) {
      if (!output[pub.publisher._id].advertorialSites[pub.advertorialSiteId]) {
        output[pub.publisher._id].advertorialSites[pub.advertorialSiteId] = {
          url: pub.advertorialSite.url,
          campaigns: {},
        };
      }

      // Find the corresponding cost for each campaign
      const costItem = costList.find(
        (cost) => cost["epom_data.totalCost"] === pub.campaignId
      );

      if (costItem) {
        output[pub.publisher._id].advertorialSites[
          pub.advertorialSiteId
          ].campaigns[pub.campaignId] = {
          cost: costItem["epom_data.totalCost"],
          name: pub.name,
        };
      }
    }
  });

  return output;
}

export function getPublisherDetails(pubList, costList) {
  // function 1: Get unique publishers
  function getPublishers(pubList) {
    const publishers = {};
    pubList.forEach((publication) => {
      if (!publishers[publication.publisher._id]) {
        publishers[publication.publisher._id] = publication.publisher.name;
      }
    });
    return publishers;
  }

  // Function 2 and 3: Get total reads, total cost, and campaign data by publisher and website
  function getWebsiteData(pubList, costList) {
    const websiteData = {};
    costList.forEach((costItem) => {
      const publisherId = costItem["epom_data.publisherId"];
      const websiteId = costItem["epom_data.websiteId"];
      const campaignId = costItem["epom_data.campaignId"];
      const cost =
        Math.round(
          (parseFloat(costItem["epom_data.totalCost"]) + Number.EPSILON) * 10
        ) / 10 || 0;
      const reads = parseInt(costItem["epom_data.Reads"]) || 0;

      if (!websiteData[publisherId]) {
        websiteData[publisherId] = {};
      }

      if (!websiteData[publisherId][websiteId]) {
        const websiteName =
          pubList.find((pub) => pub.website._id === websiteId)?.website.name ||
          websiteId;
        websiteData[publisherId][websiteId] = {
          id: websiteId,
          website: websiteName,
          reads: 0,
          revenue: 0,
          campaignData: {},
        };
      }

      websiteData[publisherId][websiteId].reads += reads;
      // Convert existing revenue and cost to Big objects and add them
      let updatedRevenue = new Big(
        websiteData[publisherId][websiteId].revenue || 0
      ).plus(new Big(cost || 0));

      // Update websiteData with the new revenue value
      websiteData[publisherId][websiteId].revenue = updatedRevenue.toNumber();

      if (!websiteData[publisherId][websiteId].campaignData[campaignId]) {
        const campaignName = pubList.find(
          (pub) => pub.campaignId === campaignId
        )?.campaign.name;
        websiteData[publisherId][websiteId].campaignData[campaignId] = {
          id: campaignId,
          name: campaignName,
          reads: 0,
          revenue: 0,
        };
      }

      websiteData[publisherId][websiteId].campaignData[campaignId].reads +=
        reads;
      websiteData[publisherId][websiteId].campaignData[campaignId].revenue +=
        cost;
    });

    // Convert campaignData to array
    Object.values(websiteData).forEach((websites) => {
      Object.values(websites).forEach((website) => {
        website.campaignData = Object.values(website.campaignData);
      });
    });

    return websiteData;
  }

  const publishers = getPublishers(pubList);
  const websiteData = getWebsiteData(pubList, costList);

  const publishersList = Object.keys(publishers).map((id) => {
    return {
      publisher: publishers[id],
      websitedata: Object.values(websiteData[id] || {}),
      // Reads and revenue of a publisher is sum of reads and revenue of its websites
      reads: Object.values(websiteData[id] || {}).reduce(
        (total, website) => total + website.reads,
        0
      ),
      revenue: `${Object.values(websiteData[id] || {})
        .reduce((total, website) => {
          // Convert both total and website.revenue to Big and then add them
          return new Big(total).plus(new Big(website.revenue || 0));
        }, new Big(0)) // Initialize the total as a Big object
        .toNumber()} DKK`,
    };
  });

  return publishersList;
}

export function mergeBilling(campaignList, costList) {
  let campaigns = campaignList.reduce((acc, campaign) => {
    acc[campaign._id] = {
      campaignName: campaign.name,
      budget: campaign.budget,
      advertiserName: campaign.advertiser.name,
    };
    return acc;
  }, {});

  let publishers = campaignList.reduce((acc, campaign) => {
    campaign.publishers.forEach((publisher) => {
      acc[publisher._id] = publisher.name;
    });
    return acc;
  }, {});

  let websites = campaignList.reduce((acc, campaign) => {
    campaign.publications.forEach((publication) => {
      acc[publication.website._id] = publication.website.name;
    });
    return acc;
  }, {});
  let output = [];
  costList.forEach((costCampaign) => {
    let campaignData; // Declare campaignData here
    if (costCampaign["epom_data.campaignId"]) {
      campaignData = {
        // Assign to campaignData
        key: costCampaign["epom_data.publicationId"],
        advertisername:
        campaigns[costCampaign["epom_data.campaignId"]]?.advertiserName,
        publisher: publishers[costCampaign["epom_data.publisherId"]],
        website: websites[costCampaign["epom_data.websiteId"]],
        reads: costCampaign["epom_data.Reads"],
        //clicks: costCampaign["epom_data.cta_clicks"],
        cost: costCampaign["epom_data.totalCost"]
          ? costCampaign["epom_data.totalCost"] + " DKK"
          : "0 DKK",
        budget: campaigns[costCampaign["epom_data.campaignId"]]?.budget
          ? campaigns[costCampaign["epom_data.campaignId"]].budget + " DKK"
          : "Not set",
        name: campaigns[costCampaign["epom_data.campaignId"]]?.campaignName
          ? campaigns[costCampaign["epom_data.campaignId"]].campaignName
          : "Not set",
      };
    } else {
      campaignData = {
        // Assign to campaignData
        advertisername: "Unknown",
        name: "Unknown",
        publisher: "Unknown",
        reads: costCampaign["epom_data.Reads"],
        //clicks: costCampaign["Params_Event.cta_clicks"],
        cost: costCampaign["epom_data.totalCost"]
          ? costCampaign["epom_data.totalCost"] + " DKK"
          : "0 DKK",
        budget: "Unknown",
      };
    }

    output.push(campaignData); // Now campaignData is visible here
  });
  return output;
}

export const exportToExcel = (tableData, workbookName) => {
  const transformedData = tableData.flatMap((item) =>
    item.trafficDrivers.map((trafficDriver) => {
      return {
        Website: item.website,
        WebsiteId: trafficDriver["aggregateTdLevel.websiteId"],
        TrafficDriverId: trafficDriver["aggregateTdLevel.trafficDriverId"],
        Headline: trafficDriver.headline,
        Publisher: item.publisher,
        Reads: trafficDriver["aggregateTdLevel.reads"],
        Spend: trafficDriver["aggregateTdLevel.publisherRevenue"],
        Impressions: trafficDriver["aggregateTdLevel.tdImpressions"],
        CreativeClicks: trafficDriver["aggregateTdLevel.tdClicks"],
        ClickRate: trafficDriver.clickRate,
      };
    })
  );

  const mainSheetData = tableData.map(({trafficDrivers, ...rest}) => rest);
  const mainWorksheet = XLSX.utils.json_to_sheet(mainSheetData);
  const trafficDriversWorksheet = XLSX.utils.json_to_sheet(transformedData);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, mainWorksheet, "Main Data");
  XLSX.utils.book_append_sheet(
    workbook,
    trafficDriversWorksheet,
    "Teasers"
  );

  const excelBuffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });
  saveAs(data, `${workbookName}.xlsx`);
};

export const exportToExcelPublishers = (tableData, workbookName) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Iterate over each publisher dataset in tableData
  tableData.forEach((publisherData, index) => {
    // Transform the campaign data
    const transformedData = publisherData.websitedata.flatMap((websiteItem) => {
      websiteItem.campaignData.map((campaign) => {
        return {
          id: websiteItem.id,
          website: websiteItem.website,
          publisher: publisherData.publisher,
          reads: campaign.reads,
          revenue: campaign.revenue,
          campaignId: campaign.id,
          campaignName: campaign.name || "N/A", // Defaulting to 'N/A' if name is not present
        };
      });
    });

    // Create a worksheet for the main publisher data (without the detailed campaign data)
    const publisherWorksheetData = publisherData.websitedata.map(
      ({campaignData, ...rest}) => rest
    );
    const publisherWorksheet = XLSX.utils.json_to_sheet(publisherWorksheetData);
    XLSX.utils.book_append_sheet(
      workbook,
      publisherWorksheet,
      `Publisher Data ${index + 1}`
    );

    // Create a worksheet for the transformed campaign data
    const campaignWorksheet = XLSX.utils.json_to_sheet(transformedData);
    XLSX.utils.book_append_sheet(
      workbook,
      campaignWorksheet,
      `Campaign Data ${index + 1}`
    );
  });

  // Add a summary sheet for top-level data
  const summaryData = tableData.map(({publisher, reads, revenue}) => ({
    publisher,
    reads,
    revenue,
  }));
  const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

  // Write the workbook to a buffer
  const excelBuffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  // Save the Excel file
  saveAs(data, `${workbookName}.xlsx`);
};

export const returnRevenueList = (revList, wList, publisher) => {
  const enrichedList = revList.map((website) => {
    return {
      "epom_data.publisher": publisher,
      "epom_data.website": wList[website["epom_data.websiteId"]],
      ...website,
    };
  });

  const totalRow = enrichedList.reduce(
    (acc, cur) => ({
      ...acc,
      "epom_data.publisher": publisher,
      "epom_data.website": "Total",
      "epom_data.totalCost": (
        parseFloat(acc["epom_data.totalCost"]) +
        parseFloat(cur["epom_data.totalCost"])
      ).toString(),
      "epom_data.totalDiscount": (
        parseFloat(acc["epom_data.totalDiscount"]) +
        parseFloat(cur["epom_data.totalDiscount"])
      ).toString(),
      "epom_data.totalRevenue": (
        parseFloat(acc["epom_data.totalRevenue"]) +
        parseFloat(cur["epom_data.totalRevenue"])
      ).toString(),
      "epom_data.Reads": (
        parseFloat(acc["epom_data.Reads"]) + parseFloat(cur["epom_data.Reads"])
      ).toString(),
      "epom_data.totalPlatformfee": (
        parseFloat(acc["epom_data.totalPlatformfee"]) +
        parseFloat(cur["epom_data.totalPlatformfee"])
      ).toString(),
      "epom_data.totalFillerPublisherEarnings": (
        parseFloat(acc["epom_data.totalFillerPublisherEarnings"]) +
        parseFloat(cur["epom_data.totalFillerPublisherEarnings"])
      ).toString(),
    }),
    {
      key: "total",
      name: "Total",
      "epom_data.totalCost": "0",
      "epom_data.totalDiscount": "0",
      "epom_data.totalRevenue": "0",
      "epom_data.Reads": "0",
      "epom_data.totalPlatformfee": "0",
      "epom_data.totalFillerPublisherEarnings": "0"
    }
  );

  // Return the enriched list with the total row appended
  return [...enrichedList, totalRow];
};

export const exportToExcelRevenue = (tableData, workbookName) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  const campaignWorksheet = XLSX.utils.json_to_sheet(tableData);
  XLSX.utils.book_append_sheet(workbook, campaignWorksheet, `Revenue Data`);

  // Write the workbook to a buffer
  const excelBuffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  // Save the Excel file
  saveAs(data, `${workbookName}.xlsx`);
};

export function processReports(reports) {
  // Initialize a Set for unique campaignIds and an object for websiteId: websiteName mappings
  const uniqueCampaignIds = new Set();
  const websiteMappings = {};
  const campaignMappings = {};
  // Iterate through each report in the array
  reports.forEach((report) => {
    // TEMPORARY: Temporary fix until we make sure we don't get null campaigns.
    if (!report.campaign) {
      return
    }
    // Add campaignId to the Set
    uniqueCampaignIds.add(report.campaignId);
    // Map websiteId to websiteName
    websiteMappings[report.websiteId] = report.website.name;
    campaignMappings[report.campaignId] = report.campaign.name;
  });

  // Convert the Set of campaignIds to an array
  const campaignIdsArray = Array.from(uniqueCampaignIds);

  // The function returns an object containing both the array and dictionary
  return {
    uniqueCampaignIds: campaignIdsArray,
    websiteMappings: websiteMappings,
    campaignMappings: campaignMappings,
  };
}

export function capitalize(word) {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function sanitize(value) {
  return isNaN(value) ? 0 : value;
}
function roundTo(value, decimals) {
  if (!value) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

const getCommonProperties = ({reads = 0, totalReadtime = 0, totalCost = 0, tdClicks = 0, publisherRevenue = 0, tdImpressions = 0, clickOuts = 0, conversions = 0, type} = {}) => ({
  avgReadtime: reads > 0 ? roundTo((totalReadtime / 1000) / reads, 1) : 0,
  clickOuts: clickOuts,
  conversions: conversions,
  cotr: reads > 0 ? roundTo((clickOuts / reads) * 100, 2) : 0,
  cpa: conversions > 0 ? roundTo(totalCost / conversions, 2) : 0,
  ctr: tdImpressions > 0 ? roundTo((tdClicks / tdImpressions) * 100, 2) : 0,
  eCpm: tdImpressions > 0 ? roundTo(publisherRevenue / (tdImpressions / 1000), 2) : 0,
  imps_reads: tdImpressions > 0 ? roundTo((reads / tdImpressions) * 1000, 2) : 0,
  publisherRevenue: roundTo(publisherRevenue, 2),
  reads: reads,
  rpa: conversions > 0 ? roundTo(publisherRevenue / conversions, 2) : 0,
  tdClicks: tdClicks,
  tdImpressions: tdImpressions,
  totalCost: roundTo(totalCost, 2),
  totalReadtime: roundTo(totalReadtime, 2),
  type,
})

const getWebsiteProperties = (item) => ({
  publicationId: item['aggregateTdLevel.publicationId'],
})

const getParsedData = (item) => ({
  clickOuts: sanitize(parseFloat(item['aggregateTdLevel.clickouts'])),
  conversions: sanitize(parseFloat(item['aggregateTdLevel.conversions'])),
  publisherRevenue: sanitize(parseFloat(item['aggregateTdLevel.publisherRevenue'])),
  reads: sanitize(parseFloat(item['aggregateTdLevel.reads'])),
  tdClicks: sanitize(parseFloat(item['aggregateTdLevel.tdClicks'])),
  tdImpressions: sanitize(parseFloat(item['aggregateTdLevel.tdImpressions'])),
  totalCost: sanitize(parseFloat(item['aggregateTdLevel.totalCost'])),
  totalReadtime: sanitize(parseFloat(item['aggregateTdLevel.total_readtime'])),
  type: item['aggregateTdLevel.tdType'],
})

const addStatistics = (currentData, newData) => {
  currentData.clickOuts += newData.clickOuts;
  currentData.conversions += newData.conversions
  currentData.publisherRevenue = roundTo(currentData.publisherRevenue + newData.publisherRevenue, 2);
  currentData.reads += newData.reads;
  currentData.tdClicks += newData.tdClicks;
  currentData.tdImpressions += newData.tdImpressions;
  currentData.totalCost = roundTo(currentData.totalCost + newData.totalCost, 2);
  currentData.totalReadtime += newData.totalReadtime;

  currentData.avgReadtime = currentData.reads > 0 ? roundTo((currentData.totalReadtime / 1000) / currentData.reads, 1) : 0;
  currentData.cotr = currentData.reads > 0 ? roundTo((currentData.clickOuts / currentData.reads) * 100, 2) : 0;
  currentData.cpa = currentData.conversions > 0 ? roundTo(currentData.totalCost / currentData.conversions, 2) : 0;
  currentData.ctr = currentData.tdImpressions > 0 ? roundTo((currentData.tdClicks / currentData.tdImpressions) * 100, 2) : 0;
  currentData.eCpm = currentData.tdImpressions > 0 ? roundTo(currentData.publisherRevenue / (currentData.tdImpressions / 1000), 2) : 0;
  currentData.imps_reads = currentData.tdImpressions > 0 ? roundTo((currentData.reads / currentData.tdImpressions) * 1000, 2) : 0;
  currentData.rpa = currentData.conversions > 0 ? roundTo(currentData.publisherRevenue / currentData.conversions, 2) : 0;
}

export function nestWebsiteTDTime(teasers, data) {
  const totals = {
    totalCost: 0,
    reads: 0,
    tdClicks: 0,
    tdImpressions: 0,
    publisherRevenue: 0,
    eCpm: 0,
    totalReadtime: 0,
    avgReadtime: 0,
    clickOuts: 0,
    imps_reads: 0,
    ctr: 0,
    cotr: 0,
  };

  const newTeasers = teasers.map((teaser) => ({
    key: teaser._id,
    name: teaser.headline,
    tdImage: teaser.imageId,
    trafficDriverId: teaser._id,
    notInitialized: true,
    ...getCommonProperties(),
    children: teaser.websites.map((website) => ({
      key: `${teaser._id}${website._id}`,
      name: website.name,
      websiteId: website._id,
      ...getCommonProperties(),
    })),
  }))

  const teaserData = data.reduce((acc, item) => {
    const teaserIndex = acc.findIndex(
      (t) => t.trafficDriverId === item['aggregateTdLevel.trafficDriverId']
    );
    const parsedData = getParsedData(item)

    if (teaserIndex !== -1) {
      const teaser = acc[teaserIndex]

      const websiteIndex = teaser.children.findIndex(
        (w) => w.websiteId === item['aggregateTdLevel.websiteId']
      );

      if (websiteIndex !== -1) {
        teaser.children[websiteIndex] = {
          ...teaser.children[websiteIndex],
          ...getWebsiteProperties(item),
          ...getCommonProperties(parsedData)
        };
      } else {
        teaser.children.push({
          key: `${item['aggregateTdLevel.trafficDriverId']}${item['aggregateTdLevel.websiteId']}`,
          removed: true,
          name: item['aggregateTdLevel.websiteName'],
          websiteId: item['aggregateTdLevel.websiteId'],
          ...getWebsiteProperties(item),
          ...getCommonProperties(parsedData)
        })
      }

      if (teaser.notInitialized) {
        delete teaser.notInitialized
        acc[teaserIndex] = {
          ...teaser,
          campaignType: item['aggregateTdLevel.campaignType'],
          ...getCommonProperties(parsedData)
        }
      } else {
        addStatistics(teaser, parsedData)
      }
    } else {
      acc.push({
        key: item['aggregateTdLevel.trafficDriverId'],
        name: `${item['aggregateTdLevel.tdHeadline']}`,
        tdImage: item['aggregateTdLevel.imageId'],
        removed: true,
        trafficDriverId: item['aggregateTdLevel.trafficDriverId'],
        ...getCommonProperties(parsedData),
        children: [{
          key: `${item['aggregateTdLevel.trafficDriverId']}${item['aggregateTdLevel.websiteId']}`,
          name: item['aggregateTdLevel.websiteName'],
          websiteId: item['aggregateTdLevel.websiteId'],
          ...getWebsiteProperties(item),
          ...getCommonProperties(parsedData)
        }]

      })
    }

    // Update totals
    addStatistics(totals, parsedData)

    return acc;
  }, newTeasers);

  return { teaserData, totals };
}

export function nestWebsiteTD(data) {
  const totals = {
    totalCost: 0,
    reads: 0,
    tdClicks: 0,
    tdImpressions: 0,
    publisherRevenue: 0,
    eCpm: 0,
  };

  const websiteData = data.reduce((acc, item) => {
    const website = acc.find(w => w.websiteId === item['aggregateTdLevel.websiteId']);
    const totalCost = sanitize(parseFloat(item['aggregateTdLevel.totalCost']));
    const reads = sanitize(parseFloat(item['aggregateTdLevel.reads']));
    const tdClicks = sanitize(parseFloat(item['aggregateTdLevel.tdClicks']));
    const tdImpressions = sanitize(parseFloat(item['aggregateTdLevel.tdImpressions']));
    const publisherRevenue = sanitize(parseFloat(item['aggregateTdLevel.publisherRevenue']));

    if (website) {
      website.children.push({
        // key: item['aggregateTdLevel.trafficDriverId'],
        key: uuid(),
        trafficDriverId: item['aggregateTdLevel.trafficDriverId'],
        name: item['aggregateTdLevel.websiteName'],
        tdImage: item['aggregateTdLevel.tdImage'],
        campaignType: item['aggregateTdLevel.campaignType'],
        date: item['aggregateTdLevel.date'],
        tdImpressions: tdImpressions,
        tdClicks: tdClicks,
        reads: reads,
        totalCost: totalCost,
        publisherRevenue: publisherRevenue,
        eCpm: Math.round((publisherRevenue / (tdImpressions / 1000)) * 100) / 100,
        imps_reads: Math.round((reads / (tdImpressions / 100)) * 1000) / 100,

      });

      website.totalCost = Math.round((website.totalCost + totalCost) * 100) / 100;
      website.reads = Math.round((website.reads + reads) * 100) / 100;
      website.tdClicks = Math.round((website.tdClicks + tdClicks) * 100) / 100;
      website.tdImpressions = Math.round((website.tdImpressions + tdImpressions) * 100) / 100;
      website.publisherRevenue = Math.round((website.publisherRevenue + publisherRevenue) * 100) / 100;
      website.eCpm = Math.round((website.publisherRevenue / (website.tdImpressions / 1000)) * 100) / 100;
      website.imps_reads = Math.round((website.reads / (website.tdImpressions / 100)) * 1000) / 100;
      website.clickOuts = Math.round((website.clickOuts * 100) / 100) ;
    } else {
      acc.push({
        key: item['aggregateTdLevel.websiteId'],
        websiteId: item['aggregateTdLevel.websiteId'],
        publicationId: item['aggregateTdLevel.publicationId'],
        name: item['aggregateTdLevel.tdHeadline'],
        totalCost: Math.round(totalCost * 100) / 100,
        reads: Math.round(reads * 100) / 100,
        tdClicks: Math.round(tdClicks * 100) / 100,
        tdImpressions: Math.round(tdImpressions * 100) / 100,
        publisherRevenue: Math.round(publisherRevenue * 100) / 100,
        eCpm: Math.round((publisherRevenue / (tdImpressions / 1000)) * 100) / 100,
        imps_reads: Math.round((reads / (tdImpressions / 100)) * 1000) / 100,
        children: [{
          // key: item['aggregateTdLevel.trafficDriverId'],
          key: uuid(),
          trafficDriverId: item['aggregateTdLevel.trafficDriverId'],
          name: item['aggregateTdLevel.websiteName'],
          tdImage: item['aggregateTdLevel.tdImage'],
          campaignType: item['aggregateTdLevel.campaignType'],
          date: item['aggregateTdLevel.date'],
          tdImpressions: tdImpressions,
          tdClicks: tdClicks,
          reads: reads,
          totalCost: totalCost,
          publisherRevenue: publisherRevenue,
          eCpm: Math.round((publisherRevenue / (tdImpressions / 1000)) * 100) / 100,
          imps_reads: Math.round((reads / (tdImpressions / 100)) * 1000) / 100
        }]
      });
    }

    // Update totals
    totals.totalCost = Math.round((totals.totalCost + totalCost) * 100) / 100;
    totals.reads = Math.round((totals.reads + reads) * 100) / 100;
    totals.tdClicks = Math.round((totals.tdClicks + tdClicks) * 100) / 100;
    totals.tdImpressions = Math.round((totals.tdImpressions + tdImpressions) * 100) / 100;
    totals.publisherRevenue = Math.round((totals.publisherRevenue + publisherRevenue) * 100) / 100;
    totals.eCpm = Math.round((totals.publisherRevenue / (totals.tdImpressions / 1000)) * 100) / 100;

    return acc;
  }, []);

  return {websiteData, totals};
}
